import {
    MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    orderBy,
    query,
} from 'firebase/firestore';
import { useCurrentUser } from '@rainbow-modules/firebase-hooks';
import { useOpenModal, useOutsideClick, useWindowResize } from '@rainbow-modules/hooks';
import { Volume2, VolumeX } from 'lucide-react';
import { InternalOverlay, RenderIf } from 'react-rainbow-components';
import useHttpMutation from 'data/firestore/useHttpMutation';
import useNotifications from 'data/firestore/agent/user/notification/useCollection';
import useStoredValue from 'data/hooks/useStoredValue';
import { EntityGet } from 'data/firestore/types';
import { Notification } from 'data/firestore/agent/user/notification/types';
import BellPlainIcon from 'components/icons/bellPlain';
import sound from '../../SMSChat/assets/notification.mp3';
import { OPPORTUNITY_DETAILS_DRAWER } from '../../../constants';
import NotificationItem from './NotificationItem';
import {
    DropdownContainer,
    EmptyState,
    Header,
    HeaderActions,
    MarkAllButton,
    NotificationMenuContainer,
    NotificationsList,
    SoundToggleButton,
    StyledBadgeOverlay,
    StyledButtonIcon,
    StyledSpinner,
    Title,
} from './styled';

interface NotificationsMenuProps {
    agentId: string;
}

type NotificationAction = (notification: EntityGet<Notification>) => void;

const NotificationsMenu = ({ agentId }: NotificationsMenuProps) => {
    const { uid: userId = '' } = useCurrentUser() || {};
    const buttonRef = useRef<any>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const unreadIdsRef = useRef<Set<string> | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [processingIds, setProcessingIds] = useState<Record<string, boolean>>({});
    const [isMarkingAll, setIsMarkingAll] = useState(false);
    const [openOpportunityDetailsModal] = useOpenModal(OPPORTUNITY_DETAILS_DRAWER);
    const {
        value: soundEnabledValue,
        save: saveSoundEnabled,
    } = useStoredValue<boolean>('notification-sound-enabled');
    const { mutateAsync: updateUserNotification } = useHttpMutation<{ status: 'read' }, unknown>({
        method: 'PATCH',
    });
    const { data: userNotifications = [], isLoading } = useNotifications(agentId, userId, {
        disabled: !agentId || !userId,
        listQueryFn: (ref) => query(
            ref,
            orderBy('createdAt', 'desc'),
        ),
        track: [agentId, userId],
    });

    useWindowResize(() => setIsOpen(false), isOpen);
    useOutsideClick(dropdownRef, () => setIsOpen(false), isOpen);

    const notifications = useMemo(
        () => userNotifications.filter((notification) => (
            notification.status === 'unread' || notification.status === 'read'
        )),
        [userNotifications],
    );

    const unreadNotifications = useMemo(
        () => notifications.filter((notification) => notification.status === 'unread'),
        [notifications],
    );

    const unreadCount = unreadNotifications.length;
    const isSoundEnabled = Boolean(soundEnabledValue);

    const badgeValue = useMemo(
        () => {
            if (!unreadCount) return undefined;
            return unreadCount > 99 ? '99+' : unreadCount;
        },
        [unreadCount],
    );

    const playNotificationSound = useCallback(async () => {
        const audio = new Audio(sound);
        try {
            await audio.play();
        } catch (error) {
            // Playback can fail due to browser autoplay restrictions.
        }
    }, []);

    useEffect(() => {
        const currentUnreadIds = new Set(
            unreadNotifications.map((notification) => notification.id),
        );
        const previousUnreadIds = unreadIdsRef.current;

        if (!previousUnreadIds) {
            unreadIdsRef.current = currentUnreadIds;
            return;
        }

        let hasNewUnread = false;
        currentUnreadIds.forEach((id) => {
            if (!previousUnreadIds.has(id)) {
                hasNewUnread = true;
            }
        });

        if (isSoundEnabled && hasNewUnread) {
            playNotificationSound();
        }

        unreadIdsRef.current = currentUnreadIds;
    }, [isSoundEnabled, playNotificationSound, unreadNotifications]);

    const icon = (
        <StyledBadgeOverlay
            overlap="circle"
            isHidden={!unreadCount}
            value={badgeValue}
        >
            <BellPlainIcon title="Notifications" />
        </StyledBadgeOverlay>
    );

    const markAsRead = useCallback(
        async (notificationId: string) => {
            if (!agentId || !userId || processingIds[notificationId]) return;

            setProcessingIds((current) => ({ ...current, [notificationId]: true }));
            try {
                await updateUserNotification({
                    pathname: `/v1/agents/${agentId}/user/${userId}/notifications/${notificationId}`,
                    body: { status: 'read' },
                });
            } finally {
                setProcessingIds((current) => {
                    const next = { ...current };
                    delete next[notificationId];
                    return next;
                });
            }
        },
        [agentId, processingIds, updateUserNotification, userId],
    );

    const handleMarkAllAsRead = useCallback(
        async () => {
            if (!unreadNotifications.length || isMarkingAll) return;

            setIsMarkingAll(true);
            await Promise.allSettled(
                unreadNotifications.map((notification) => (
                    updateUserNotification({
                        pathname: `/v1/agents/${agentId}/user/${userId}/notifications/${notification.id}`,
                        body: { status: 'read' },
                    })
                )),
            );
            setIsMarkingAll(false);
        },
        [agentId, isMarkingAll, unreadNotifications, updateUserNotification, userId],
    );

    const openNotification = useCallback(
        async (notification: EntityGet<Notification>) => {
            if (notification.status === 'unread') {
                await markAsRead(notification.id);
            }

            const actionsByType: Record<string, NotificationAction> = {
                'note-created': (currentNotification) => {
                    const opportunityId = currentNotification?.entityRef?.id;
                    if (!opportunityId) return;

                    openOpportunityDetailsModal({
                        data: {
                            opportunityId,
                            activeTab: 'notes',
                        },
                    });
                },
            };

            const defaultAction: NotificationAction = () => {};
            const action = actionsByType[notification.type] || defaultAction;
            action(notification);
            setIsOpen(false);
        },
        [markAsRead, openOpportunityDetailsModal],
    );

    const handleMarkReadClick = useCallback(
        async (event: MouseEvent<HTMLButtonElement>, notificationId: string) => {
            event.stopPropagation();
            event.preventDefault();
            await markAsRead(notificationId);
        },
        [markAsRead],
    );

    const handleSoundToggle = useCallback(
        () => {
            saveSoundEnabled(!isSoundEnabled);
        },
        [isSoundEnabled, saveSoundEnabled],
    );

    return (
        <NotificationMenuContainer>
            <RenderIf isTrue={isLoading}>
                <StyledSpinner type="arc" variant="brand">
                    <StyledButtonIcon icon={icon} />
                </StyledSpinner>
            </RenderIf>
            <RenderIf isTrue={!isLoading}>
                <StyledButtonIcon
                    ref={buttonRef}
                    icon={icon}
                    onClick={() => setIsOpen(!isOpen)}
                />
                <InternalOverlay
                    isVisible={isOpen}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    triggerElementRef={() => buttonRef.current?.htmlElementRef}
                >
                    <DropdownContainer ref={dropdownRef}>
                        <Header>
                            <Title>Notifications</Title>
                            <HeaderActions>
                                <SoundToggleButton
                                    type="button"
                                    onClick={handleSoundToggle}
                                    aria-label={isSoundEnabled ? 'Disable notification sound' : 'Enable notification sound'}
                                    title={isSoundEnabled ? 'Sound on' : 'Sound off'}
                                >
                                    <RenderIf isTrue={isSoundEnabled}>
                                        <Volume2 size={16} />
                                    </RenderIf>
                                    <RenderIf isTrue={!isSoundEnabled}>
                                        <VolumeX size={16} />
                                    </RenderIf>
                                </SoundToggleButton>
                                <MarkAllButton
                                    type="button"
                                    disabled={!unreadCount || isMarkingAll}
                                    onClick={handleMarkAllAsRead}
                                >
                                    Mark all as read
                                </MarkAllButton>
                            </HeaderActions>
                        </Header>
                        <RenderIf isTrue={notifications.length > 0}>
                            <NotificationsList>
                                {notifications.map((notification) => {
                                    const isProcessing = Boolean(processingIds[notification.id]);
                                    return (
                                        <NotificationItem
                                            key={notification.id}
                                            notification={notification}
                                            isProcessing={isProcessing}
                                            onOpen={openNotification}
                                            onMarkRead={handleMarkReadClick}
                                        />
                                    );
                                })}
                            </NotificationsList>
                        </RenderIf>
                        <RenderIf isTrue={!notifications.length}>
                            <EmptyState>No notifications yet.</EmptyState>
                        </RenderIf>
                    </DropdownContainer>
                </InternalOverlay>
            </RenderIf>
        </NotificationMenuContainer>
    );
};

export default NotificationsMenu;
